import { ref, watch } from 'vue';
import type { Coordinates } from '@/api';
import { createSubscription } from '@/api';
import { useI18n } from 'vue-i18n';
import { isValidationProblemDetails } from '@/common/api.ts';

export type SubscriptionContext = {
  location: Coordinates | null;
  distance: number;
  categoryIds: number[];
};

export type SubscriptionContextStatus = 'resolving' | 'ready' | 'error';

export function useSubscription(getContext: () => SubscriptionContext, getContextStatus: () => SubscriptionContextStatus = () => 'ready') {
  const { t } = useI18n();

  const email = ref('');
  const isLoading = ref(false);
  const isSuccess = ref(false);
  const errorMessage = ref('');
  const isModalOpen = ref(false);

  const handleSubmit = () => {
    if (!email.value || isLoading.value || getContextStatus() !== 'ready') {
      return;
    }

    isModalOpen.value = true;
  };

  const handleTokenReceived = async (token: string) => {
    if (isLoading.value || getContextStatus() !== 'ready') {
      return;
    }

    isLoading.value = true;
    errorMessage.value = '';

    try {
      const context = getContext();
      const result = await createSubscription({
        body: {
          email: email.value,
          token: token,
          location: context.location ?? undefined,
          distance: context.location ? context.distance : undefined,
          categoryIds: context.categoryIds.length > 0 ? context.categoryIds : undefined,
        },
      });

      if (result.response?.ok) {
        isSuccess.value = true;
        email.value = '';
        return;
      }
      if (result.error && isValidationProblemDetails(result.error) && result.error.errors) {
        for (const [key, messages] of Object.entries(result.error.errors)) {
          if (!messages[0]) continue;

          if (key === 'email' || key === 'token') {
            errorMessage.value = messages[0];
            return;
          }
        }
      }
      errorMessage.value = t('components.subscribe.error');
    } catch {
      errorMessage.value = t('components.subscribe.error');
    } finally {
      isLoading.value = false;
    }
  };

  const handleModalClose = () => {
    isModalOpen.value = false;
  };

  watch(getContextStatus, (status) => {
    if (status !== 'ready') {
      isModalOpen.value = false;
    }
  });

  return { email, isLoading, isSuccess, errorMessage, isModalOpen, handleSubmit, handleTokenReceived, handleModalClose };
}
